import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';
import { GetSessionJobDetails } from '../../../Apis/Facility';
import Spinner from '../../CustomComponents/Spinner';
import CustomAvatar from '../../CustomComponents/CustomAvatar';
import {
  DetailColumn,
  DetailBar,
  DetailTitleWrap,
  DetailTitle,
  DetailSub,
  Acts,
  DetailCloseButton,
  StatusBadge,
  HeaderCard,
  FactGrid,
  FactKey,
  FactVal,
  Panel,
  EmptyState
} from './AuditDetailStyles';

const PAGE_SIZE = 13;
const GRID = '1.1fr 1.2fr 1.2fr 1.2fr 1.6fr';

const headStyle = {
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 12,
  padding: '8px 0',
  borderBottom: '1px solid #E8ECF0',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#525E6F'
};
const rowStyle = {
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 12,
  padding: '10px 0',
  borderBottom: '1px solid #F0F2F5',
  fontSize: 13,
  color: '#1A2332'
};
const cellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const Fact = ({ label, value }) => (
  <div>
    <FactKey>{label}</FactKey>
    <FactVal>{value || '—'}</FactVal>
  </div>
);

/**
 * User session detail (audit split view). Restyles the old drawer content:
 * a fact card of the login session plus the job-audit actions recorded during
 * it (GetSessionJobDetails, paged with "Load more").
 */
const AuditUserDetail = ({ session, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const userSessionId = session?.id;

  const load = useCallback(
    pageNo => {
      if (!userSessionId) return;
      if (pageNo === 0) setLoading(true);
      else setMoreLoading(true);
      GetSessionJobDetails(userSessionId, pageNo, PAGE_SIZE, '')
        .then(res => {
          if (res.data.success) {
            const result = res.data.result || [];
            setRows(prev => (pageNo === 0 ? result : prev.concat(result)));
            setHasMore(result.length >= PAGE_SIZE);
          }
        })
        .catch(err => console.log(err))
        .finally(() => {
          setLoading(false);
          setMoreLoading(false);
        });
    },
    [userSessionId]
  );

  useEffect(() => {
    setRows([]);
    setPage(0);
    load(0);
  }, [load]);

  if (!session) {
    return (
      <DetailColumn>
        <EmptyState style={{ paddingTop: 80 }}>
          Select a user from the list to view their session details.
        </EmptyState>
      </DetailColumn>
    );
  }

  const failed = session.loggingStatus === 'FAILED';
  const logout =
    session.logoutTime === 0
      ? failed
        ? '—'
        : 'Active Session'
      : session.logoutTime
      ? formatDateTime(session.logoutTime)
      : '—';

  return (
    <DetailColumn>
      <DetailBar>
        <DetailTitleWrap>
          <CustomAvatar url={session.profilePic} size={34} />
          <div>
            <DetailTitle style={{ fontSize: 20 }}>
              {session.fullName || 'User'}
            </DetailTitle>
            <DetailSub>{session.email || '—'}</DetailSub>
          </div>
          <StatusBadge status={failed ? 'Failed' : 'Completed'}>
            {(session.loggingStatus || '—').toLowerCase()}
          </StatusBadge>
        </DetailTitleWrap>
        <Acts>
          <DetailCloseButton
            type='button'
            aria-label='Close user details'
            onClick={onClose}
          >
            <CloseOutlined />
          </DetailCloseButton>
        </Acts>
      </DetailBar>

      <HeaderCard>
        <FactGrid>
          <Fact label='Name' value={session.fullName} />
          <Fact label='Email' value={session.email} />
          <Fact label='User Type' value={session.userType} />
          <Fact label='IP Address' value={session.loggedUserIp} />
          <Fact
            label='Login Time'
            value={
              session.loginTime ? formatDateTime(session.loginTime) : '—'
            }
          />
          <Fact label='Logout Time' value={logout} />
          <Fact label='Status' value={session.loggingStatus} />
        </FactGrid>
      </HeaderCard>

      <Panel>
        <h3>Session activity</h3>
        <p className='sub'>Jobs and actions recorded during this session.</p>
        {loading ? (
          <Spinner height={'180px'} />
        ) : rows.length ? (
          <div>
            <div style={headStyle}>
              <span>Job ID</span>
              <span>Action</span>
              <span>Start</span>
              <span>End</span>
              <span>Comment</span>
            </div>
            {rows.map((r, i) => (
              <div key={i} style={rowStyle}>
                <span style={cellStyle}>{r.jobId || r.userName || '—'}</span>
                <span style={cellStyle}>{r.status || r.action || '—'}</span>
                <span style={cellStyle}>
                  {r.startTime ? formatDate(r.startTime) : 'N/A'}
                </span>
                <span style={cellStyle}>
                  {r.endTime ? formatDate(r.endTime) : 'N/A'}
                </span>
                <span style={cellStyle}>{r.comment || '—'}</span>
              </div>
            ))}
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Button
                  size='small'
                  loading={moreLoading}
                  onClick={() => {
                    const next = page + 1;
                    setPage(next);
                    load(next);
                  }}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        ) : (
          <EmptyState>No session activity recorded.</EmptyState>
        )}
      </Panel>
    </DetailColumn>
  );
};

export default AuditUserDetail;
